import { defineChart } from '..'
import { height, width } from '@/composables/useImageExport'
import { tooltip } from '@/composables/useChart/theme'
import type { BarSeriesOption } from 'echarts'
import type { CartesianAxisOption } from 'echarts/types/src/coord/cartesian/AxisModel'
import type { Values } from '@/composables/useValues'

export type Series = {
    name: string
    maxDisplay?: number
    max: number | undefined
    type: 'log' | 'value'
    formatter: Values['formatter']
    valueKey: Values['valueKey']
}

type Size = Partial<{ width: number; height: number }>

type Params = [Values, Series[], Size]

export default defineChart<Params>((values, series, size) => {
    const { countryData, dimension, compareCountryData } = values

    if (!countryData || !dimension || !size.width || !size.height) return {}

    const absoluteMax =
        Math.max(countryData.value, compareCountryData?.value ?? 0) * 1.1

    series.forEach(series => {
        if (series.type === 'value') {
            series.max = absoluteMax
        }
    })

    return {
        tooltip: {},
        grid: {
            top: 90
        },
        xAxis: [createCategoryAxis(), createCategoryAxis()],
        yAxis: [
            createValueAxis(0, series, 'left'),
            createValueAxis(1, series, 'right')
        ],
        graphic: [createHeading(series, countryData, size.width, size.height)],
        export: {
            graphic: [
                createHeading(series, countryData, width, height * 0.825, '10%')
            ],
            grid: {
                top: '25%',
                bottom: '10%'
            }
        },
        series: [
            ...createSeries(
                0,
                series,
                dimension,
                countryData,
                compareCountryData
            ),
            ...createSeries(
                1,
                series,
                dimension,
                countryData,
                compareCountryData
            )
        ]
    }
})

/**
 * Create two bar series for both
 * the background and the actual value bars.
 */
function createSeries(
    index: number,
    series: Series[],
    dimension: Dimension,
    value: Value,
    compareValue?: Value
) {
    const { name, formatter, valueKey } = series[index]

    const backroundData: number[] = Array.from({ length: 3 })
    backroundData.splice(index + 1, 0)

    const data: number[] = Array.from({ length: 3 })
    data.splice(index + 1, 0, value[valueKey])

    const { getName } = useCountries()

    return [
        {
            type: 'bar',
            xAxisIndex: index,
            yAxisIndex: index,
            data: backroundData,
            animation: false,
            silent: true,
            barGap: '-100%',
            barCategoryGap: 0,
            barMaxWidth: 60,
            itemStyle: {
                color: twColor('neutral')
            }
        },
        {
            type: 'bar',
            xAxisIndex: index,
            yAxisIndex: index,
            barGap: '-100%',
            barCategoryGap: 0,
            barMaxWidth: 60,
            itemStyle: {
                color: dimension.countryColor
            },
            tooltip: {
                formatter: tooltip(name, value.iso, formatter(value[valueKey]))
            },
            data,
            markPoint: {
                symbol: 'rect',
                itemStyle: {
                    color: dimension.comparisonCountryColor
                },
                symbolSize: [60, 5],
                silent: true,
                label: {
                    show: true,
                    color: 'black',
                    position: 'top',
                    fontWeight: 'bold',
                    width: 80,
                    align: 'center',
                    overflow: 'truncate',
                    formatter(params) {
                        const name = getName(params.name) ?? ''
                        return name.length > 20 ? params.name : name
                    }
                },
                data: compareValue
                    ? [
                          {
                              name: compareValue.iso,
                              xAxis: index + 1,
                              yAxis: compareValue[valueKey]
                          }
                      ]
                    : []
            }
        }
    ] satisfies BarSeriesOption[]
}

/**
 * Create a value axis.
 */
function createValueAxis(
    index: number,
    series: Series[],
    align: 'left' | 'right'
) {
    const { max, type, formatter } = series[index]

    return {
        type: series[index].type,
        max,
        min: type === 'log' ? 1 : 0,
        splitLine: {
            show: true,
            lineStyle: { type: 'dotted', color: 'black' }
        },
        axisLine: {
            show: false
        },
        axisTick: {
            show: false
        },
        alignTicks: index === 0,
        axisLabel: {
            show: true,
            align,
            fontSize: 15,
            padding: [0, 0, 5, 0],
            margin: 0,
            showMaxLabel: true,
            baseline: 'bottom',
            // @ts-expect-error fine
            formatter(value, index) {
                if (index === 0) return 0
                return formatter(Number(value))
            }
        }
    } satisfies CartesianAxisOption
}

/**
 * Create a category axis.
 */
function createCategoryAxis() {
    return {
        type: 'category',
        show: false,
        axisLabel: {
            show: false
        }
    } satisfies CartesianAxisOption
}

/**
 * Create the heading text.
 */
function createHeadingText(
    series: Series,
    value: Value,
    width: number,
    align: 'right' | 'left'
) {
    const { name, valueKey, formatter } = series

    return {
        type: 'text',
        x: width / 2,
        style: {
            text: `${name.toUpperCase()}\n{value|${formatter(
                value[valueKey]
            ).toUpperCase()}}`,
            align,
            width: (width / 2) * 0.9,
            overflow: 'truncate',
            padding: [
                10,
                align === 'left' ? 0 : 10,
                10,
                align === 'left' ? 10 : 0
            ],
            fontSize: 14,
            fontFamily: 'Source Sans Pro',
            rich: {
                value: {
                    fontSize: 24,
                    fontFamily: 'Source Sans Pro',
                    fontWeight: 'bold',
                    padding: [10, 0, 0, 0]
                }
            }
        }
    }
}

/**
 * Create dotted line.
 */
function createDottedLine(width: number, height: number) {
    return {
        type: 'line',
        zlevel: 1000,
        x: width / 2,
        shape: {
            y2: height
        },
        style: {
            lineDash: 'dotted'
        }
    }
}

/**
 * Create heading graphic element.
 */
function createHeading(
    series: Series[],
    current: Value,
    width = 1,
    height = 1,
    top: string | number = 0
) {
    return {
        type: 'group',
        silent: true,
        top,
        children: [
            createDottedLine(width, height),
            createHeadingText(series[0], current, width, 'right'),
            createHeadingText(series[1], current, width, 'left')
        ]
    }
}
